import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { IObjectWithToString } from "./useLocalStorageItem";

export interface ICookieOptions {
    path?: string;
    expires?: number | Date;
    secure?: boolean;
    sameSite?: "Strict" | "Lax" | "None";
}

export const useCookieItem = <T extends IObjectWithToString | null>(
    key: string,
    fallbackValue: T,
    options: ICookieOptions = {},
    serialize: (value: T) => string,
    deserialize: (value: string) => T,
): readonly [T, Dispatch<SetStateAction<T>>] => {
    const [value, setValue] = useState(() => {
        const cookie = decodeURIComponent(document.cookie)
            .split("; ")
            .find((row) => row.startsWith(`${key}=`));
        return cookie ? deserialize(cookie.split("=")[1]) : fallbackValue;
    });

    useEffect(() => {
        const cookieString = `${key}=${encodeURIComponent(serialize(value))}`;
        const cookieOptions = Object.entries(options)
            .map(([option, value]) => `${option}=${value}`)
            .join("; ");

        document.cookie = `${cookieString}; ${cookieOptions}`;
    }, [key, options, serialize, value]);

    return [value, setValue] as const;
};
